<template>
  <span
    class="wc-icon"
    :class="{ 'wc-icon--custom-size': hasCustomDimensions }"
    :style="iconStyle"
    :aria-hidden="ariaHidden"
    :aria-label="computedAriaLabel"
    :role="computedRole"
    v-html="svgMarkup"
  />
</template>

<script setup lang="ts">
import { computed, defineProps } from 'vue';
import type { CSSProperties, PropType } from 'vue';
import icons from './icons';

type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> & U[keyof U];

const SIZE_CLASS_VALUES = ['3xs', '2xs', 'xs', 'sm', 'md', 'lg', 'xl', '2x', '3x'] as const;
type SizeClass = (typeof SIZE_CLASS_VALUES)[number];

interface FullDimensionsRem {
  widthRem: number;
  heightRem: number;
}

type DimensionsRem = AtLeastOne<FullDimensionsRem>;
type IconSize = DimensionsRem | SizeClass;

const ICON_SIZE_MAP: Record<SizeClass, string> = {
  '3xs': '0.625rem',
  '2xs': '0.6875rem',
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.25rem',
  xl: '1.5rem',
  '2x': '2rem',
  '3x': '3rem',
};

const WC_ICON_ASPECT_RATIO_VAR = '--wc-icon-aspect-ratio' as const;

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  ariaLabel: {
    type: String,
    default: null,
  },
  iconSize: {
    type: [String, Object] as PropType<IconSize>,
    default: null,
  },
  aspectRatio: {
    type: String,
    default: null,
  },
});

const toRem = (value?: number): string | null => (typeof value === 'number' ? `${value}rem` : null);

const hasAriaLabel = computed(() => !!props.ariaLabel?.trim());
const computedRole = computed(() => (hasAriaLabel.value ? 'img' : undefined));
const computedAriaLabel = computed(() => (hasAriaLabel.value ? props.ariaLabel : undefined));
const ariaHidden = computed(() => (hasAriaLabel.value ? undefined : 'true'));

const hasCustomDimensions = computed(() => !!(props.iconSize || props.aspectRatio));

const iconStyle = computed(() => {
  const style: CSSProperties = {};

  if (typeof props.iconSize === 'string') {
    const resolvedSize = ICON_SIZE_MAP[props.iconSize as SizeClass];

    if (resolvedSize) {
      style.width = resolvedSize;
      style.height = resolvedSize;
    }
  } else if (props.iconSize && typeof props.iconSize === 'object') {
    const width = toRem(props.iconSize.widthRem);
    const height = toRem(props.iconSize.heightRem);

    if (width || height) {
      style.width = width || 'auto';
      style.height = height || 'auto';
    }
  }

  if (props.aspectRatio) {
    style[WC_ICON_ASPECT_RATIO_VAR] = props.aspectRatio;
  }

  if (!Object.keys(style).length) {
    return undefined;
  }

  return style;
});

const svgMarkup = computed(() => {
  const svg = icons[props.name];

  if (!svg) {
    console.warn(`[WCSvgIcon] Icon "${props.name}" not found in icons.json`);
    return '';
  }

  return svg;
});
</script>

<style scoped>
.wc-icon {
  --wc-icon-aspect-ratio: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: -0.125em;
  line-height: 1;
}

.wc-icon :deep(svg) {
  display: block;
}

.wc-icon--custom-size :deep(svg) {
  width: 100% !important;
  height: 100% !important;
}

.wc-icon--custom-size {
  aspect-ratio: var(--wc-icon-aspect-ratio, auto);
}
</style>
